import { update as updatePreferencesRoute } from '@/routes/profile/preferences';
import { router, usePage } from '@inertiajs/vue3';
import {
    computed,
    effectScope,
    getCurrentScope,
    inject,
    onMounted,
    onScopeDispose,
    ref,
    watch,
} from 'vue';

export type Theme = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'theme';
const currentTheme = ref<Theme>('system');
const systemIsDark = ref(false);
const isDark = computed(
    () =>
        currentTheme.value === 'dark' ||
        (currentTheme.value === 'system' && systemIsDark.value),
);
let consumers = 0;
let stopSynchronization: (() => void) | undefined;

function visitorTheme(): Theme {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === 'dark' || stored === 'light') return stored;
    } catch {
        // Le choix reste utilisable en mémoire lorsque le navigateur bloque le stockage.
    }
    return 'system';
}

function applyTheme(theme: Theme) {
    currentTheme.value = theme;
    document.documentElement.classList.toggle('dark', isDark.value);
    try {
        if (theme === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
        else localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // Le stockage local est facultatif, y compris pour une préférence serveur.
    }
}

export const HYDRATING_THEME = Symbol('hydrating-theme');

export function initializeHydratedTheme(theme: Theme = 'system') {
    currentTheme.value = theme;
    systemIsDark.value = false;
}

export function useTheme() {
    const page = usePage();
    if (typeof window === 'undefined') {
        const serverTheme = ref<Theme>(
            (page.props.auth?.user?.preferences?.theme as Theme) || 'system',
        );
        return {
            currentTheme: serverTheme,
            isDark: computed(() => serverTheme.value === 'dark'),
            setTheme: (theme: Theme) => {
                serverTheme.value = theme;
            },
            initTheme: () => {},
            syncFromServer: () => {},
        };
    }
    const hydrating = inject(HYDRATING_THEME, false);

    function syncFromServer() {
        const user = page.props?.auth?.user;
        applyTheme(
            user
                ? (user.preferences?.theme as Theme) || 'light'
                : visitorTheme(),
        );
    }

    function startSynchronization() {
        if (consumers === 0) {
            const scope = effectScope(true);
            scope.run(() => {
                const media = window.matchMedia('(prefers-color-scheme: dark)');
                systemIsDark.value = media.matches;
                const onSystemChange = (event: MediaQueryListEvent) => {
                    systemIsDark.value = event.matches;
                    document.documentElement.classList.toggle(
                        'dark',
                        isDark.value,
                    );
                };
                media.addEventListener('change', onSystemChange);
                onScopeDispose(() =>
                    media.removeEventListener('change', onSystemChange),
                );

                watch(
                    () =>
                        page.props
                            ? `${page.props.auth?.user?.id ?? ''}:${page.props.auth?.user?.preferences?.theme ?? ''}`
                            : null,
                    () => {
                        if (page.props) syncFromServer();
                    },
                    { immediate: true, flush: 'sync' },
                );
            });
            stopSynchronization = () => scope.stop();
        }
        consumers++;
        onScopeDispose(() => {
            consumers--;
            if (consumers === 0) {
                stopSynchronization?.();
                stopSynchronization = undefined;
            }
        });
    }

    if (getCurrentScope()) {
        if (hydrating) onMounted(startSynchronization);
        else startSynchronization();
    }

    function setTheme(theme: Theme) {
        applyTheme(theme);
        const user = page.props?.auth?.user;
        if (!user) return;

        const prefs = user.preferences;
        router.put(
            updatePreferencesRoute().url,
            {
                language: prefs?.language ?? 'fr',
                timezone: prefs?.timezone ?? 'Europe/Paris',
                theme,
            },
            {
                preserveScroll: true,
                preserveState: true,
                onError: syncFromServer,
            },
        );
    }

    return {
        currentTheme,
        isDark,
        setTheme,
        initTheme: syncFromServer,
        syncFromServer,
    };
}

export default useTheme;
